import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Search, Copy, Send, Check, X,
  Link2, ChevronDown, ChevronRight, Plus, Download, Save, Upload, Trash2, Pencil,
} from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";

const STATUS = { not_invited: "Not invited", ready_to_send: "Ready", sent: "Sent" };
const surface = "bg-[#241812] border border-[#3a2a20] rounded-lg";
const selCls = "bg-[#1c120d] border border-[#3a2a20] rounded-md px-3 py-2 text-sm text-stone-200";

export default function Dashboard() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [households, setHouseholds] = useState([]);
  const [filters, setFilters] = useState({ tier: "", invite_status: "", attendance: "", search: "" });
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const loadStats = useCallback(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  const loadHouseholds = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filters.tier) params.tier = filters.tier;
    if (filters.invite_status) params.invite_status = filters.invite_status;
    if (filters.attendance) params.attendance = filters.attendance;
    if (filters.search) params.search = filters.search;
    api.get("/admin/households", { params })
      .then((r) => setHouseholds(r.data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { const t = setTimeout(loadHouseholds, 250); return () => clearTimeout(t); }, [loadHouseholds]);

  const refresh = () => { loadStats(); loadHouseholds(); };
  const doLogout = async () => { await logout(); navigate("/admin/login"); };
  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const batchActivate = async (activate) => {
    if (!selected.size) return toast.error("Select at least one household");
    await api.post("/admin/activate", { household_ids: [...selected], activate });
    toast.success(`${selected.size} household(s) ${activate ? "activated" : "deactivated"}`);
    setSelected(new Set()); refresh();
  };
  const batchSent = async () => {
    if (!selected.size) return toast.error("Select at least one household");
    await api.post("/admin/mark-sent", { household_ids: [...selected] });
    toast.success(`Marked ${selected.size} as sent`);
    setSelected(new Set()); refresh();
  };

  return (
    <div className="min-h-screen bg-[#1a110c] text-stone-200 font-sans">
      <header className="sticky top-0 z-40 bg-[#1a110c]/95 backdrop-blur border-b border-[#3a2a20] px-5 sm:px-8 py-4 flex items-center justify-between">
        <div>
          <p className="text-lg font-semibold text-stone-100 tracking-tight">Guest Management</p>
          <p className="text-xs text-stone-500">{user?.username || user?.email}</p>
        </div>
        <button onClick={doLogout} className="inline-flex items-center gap-2 text-stone-400 hover:text-gold text-sm" data-testid="logout-btn">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        {stats && <StatsGrid stats={stats} />}

        <Tabs defaultValue="guests" className="mt-8">
          <TabsList className="bg-[#241812] border border-[#3a2a20] flex-wrap h-auto">
            <TabsTrigger value="guests" data-testid="tab-guests">Guests</TabsTrigger>
            <TabsTrigger value="import" data-testid="tab-import">Import CSV</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Site &amp; Settings</TabsTrigger>
            <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
          </TabsList>

          <TabsContent value="guests" className="mt-6">
            <Filters filters={filters} setFilters={setFilters} />
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="text-sm text-stone-400">{households.length} households · {selected.size} selected</span>
              <div className="flex flex-wrap gap-2 ml-auto">
                <ActionBtn onClick={() => batchActivate(true)} testid="batch-activate">Activate</ActionBtn>
                <ActionBtn onClick={() => batchActivate(false)} testid="batch-deactivate">Deactivate</ActionBtn>
                <ActionBtn onClick={batchSent} testid="batch-mark-sent"><Send className="w-3.5 h-3.5" /> Mark sent</ActionBtn>
                <AddHouseholdDialog onDone={refresh} />
              </div>
            </div>
            <div className="mt-4 space-y-2">
              {loading && <p className="text-stone-500 text-center py-8">Loading...</p>}
              {!loading && households.length === 0 && <p className="text-stone-500 text-center py-8">No households match these filters.</p>}
              {households.map((hh) => (
                <HouseholdRow key={hh.id} hh={hh} selected={selected.has(hh.id)} onSelect={() => toggleSelect(hh.id)} onChange={refresh} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="import" className="mt-6"><ImportTab onDone={refresh} /></TabsContent>
          <TabsContent value="settings" className="mt-6"><SettingsTab /></TabsContent>
          <TabsContent value="activity" className="mt-6"><ActivityTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function StatsGrid({ stats }) {
  const cards = [
    { label: "They're in", value: stats.attending, hint: "people attending" },
    { label: "They're out", value: stats.declined, hint: "people declined" },
    { label: "No response yet", value: stats.awaiting, hint: "people awaiting" },
    { label: "Active invited places", value: stats.active_invited_places, hint: `${stats.active_households} households` },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="stats-grid">
      {cards.map((c) => (
        <div key={c.label} className={`${surface} p-4`}>
          <p className="text-3xl font-semibold text-gold">{c.value}</p>
          <p className="text-sm text-stone-200 mt-1">{c.label}</p>
          <p className="text-xs text-stone-500">{c.hint}</p>
        </div>
      ))}
    </div>
  );
}

function Filters({ filters, setFilters }) {
  return (
    <div className="flex flex-wrap gap-3 items-center">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="w-4 h-4 text-stone-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input placeholder="Search guests or households..." value={filters.search} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} className="pl-9 bg-[#241812] border-[#3a2a20] text-stone-200" data-testid="search-input" />
      </div>
      <select className={selCls} value={filters.tier} onChange={(e) => setFilters((f) => ({ ...f, tier: e.target.value }))} data-testid="filter-tier">
        <option value="">All tiers</option>
        {["A", "B", "C"].map((t) => <option key={t} value={t}>{`Tier ${t}`}</option>)}
      </select>
      <select className={selCls} value={filters.invite_status} onChange={(e) => setFilters((f) => ({ ...f, invite_status: e.target.value }))} data-testid="filter-status">
        <option value="">All invite status</option>
        {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <select className={selCls} value={filters.attendance} onChange={(e) => setFilters((f) => ({ ...f, attendance: e.target.value }))} data-testid="filter-attendance">
        <option value="">All responses</option>
        <option value="attending">Has attending</option>
        <option value="declined">All declined</option>
        <option value="awaiting">Has awaiting</option>
        <option value="mixed">Mixed</option>
      </select>
    </div>
  );
}

function ActionBtn({ children, onClick, testid, danger }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm transition-colors ${danger ? "border-red-500/40 text-red-400 hover:bg-red-500/10" : "border-[#3a2a20] text-stone-200 hover:border-gold hover:text-gold"}`}>
      {children}
    </button>
  );
}

function Badge({ children, tone = "dim" }) {
  const tones = {
    gold: "border-gold text-gold",
    dim: "border-[#3a2a20] text-stone-400",
    green: "border-emerald-500/50 text-emerald-400",
    red: "border-red-500/40 text-red-400",
  };
  return <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${tones[tone]}`}>{children}</span>;
}

function replyPill(a, d, w, inviteStatus) {
  const total = a + d + w;
  if (total === 0) return { label: "No guests", cls: "border-stone-600 text-stone-500" };
  if (w === total) {
    if (inviteStatus === "sent") return { label: "Awaiting reply", cls: "border-amber-500/60 text-amber-400 bg-amber-500/10" };
    return { label: "Not sent", cls: "border-stone-500 text-stone-400" };
  }
  if (a > 0 && d === 0 && w === 0) return { label: "They're in", cls: "border-emerald-500 text-emerald-400 bg-emerald-500/10" };
  if (d > 0 && a === 0 && w === 0) return { label: "They're not in", cls: "border-red-500/60 text-red-400 bg-red-500/10" };
  return { label: `${a} in · ${d} out${w ? ` · ${w} awaiting` : ""}`, cls: "border-gold text-gold bg-gold/10" };
}

function EditableName({ g, onSave }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(g.name || "");
  useEffect(() => { setV(g.name || ""); }, [g.name]);
  if (editing || !g.name) {
    return (
      <span className="inline-flex items-center gap-2">
        <Input value={v} onChange={(e) => setV(e.target.value)} placeholder={g.is_plus_one ? "Unnamed plus one" : "Add name"} className="h-8 w-44 bg-[#241812] border-[#3a2a20] text-stone-200 text-sm" data-testid={`name-edit-${g.id}`} />
        <button onClick={() => { onSave(v); setEditing(false); }} className="text-gold" data-testid={`name-edit-${g.id}-save`}><Save className="w-4 h-4" /></button>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2">
      <span className="text-stone-100">{g.name}</span>
      <button onClick={() => setEditing(true)} className="text-stone-500 hover:text-gold" data-testid={`name-edit-toggle-${g.id}`} title="Edit name"><Pencil className="w-3.5 h-3.5" /></button>
    </span>
  );
}

function HouseholdRow({ hh, selected, onSelect, onChange }) {
  const [open, setOpen] = useState(false);
  const [editName, setEditName] = useState(false);
  const [nameVal, setNameVal] = useState(hh.invite_name);
  useEffect(() => { setNameVal(hh.invite_name); }, [hh.invite_name]);
  const attending = hh.guests.filter((g) => g.attendance === "attending").length;
  const declined = hh.guests.filter((g) => g.attendance === "declined").length;
  const awaiting = hh.guests.filter((g) => g.attendance === "awaiting").length;

  const patchGuest = async (gid, body) => { await api.patch(`/admin/guests/${gid}`, body); onChange(); };
  const deleteGuest = async (gid, name) => {
    if (!window.confirm(`Remove ${name || "this guest"} completely?`)) return;
    await api.delete(`/admin/guests/${gid}`); toast.success("Guest removed"); onChange();
  };
  const deleteHousehold = async () => {
    if (!window.confirm(`Delete the entire "${hh.invite_name}" household and all its guests? This cannot be undone.`)) return;
    await api.delete(`/admin/households/${hh.id}`); toast.success("Household deleted"); onChange();
  };
  const toggleActive = async () => { await api.patch(`/admin/households/${hh.id}`, { is_active: !hh.is_active }); toast.success(hh.is_active ? "Deactivated" : "Activated"); onChange(); };
  const markSent = async () => { await api.post("/admin/mark-sent", { household_ids: [hh.id] }); toast.success("Marked as sent"); onChange(); };
  const copyMessage = async () => {
    const { data } = await api.get(`/admin/households/${hh.id}/message`);
    const ok = await copyText(data.message);
    if (ok) { toast.success("Invitation message copied"); }
    else { window.prompt("Copy the invitation message below (press Ctrl/Cmd+C):", data.message); }
  };
  const copyLink = async () => {
    const link = `${window.location.origin}/rsvp/${hh.token}`;
    const ok = await copyText(link);
    if (ok) { toast.success("Link copied"); }
    else { window.prompt("Copy the personal link below (press Ctrl/Cmd+C):", link); }
  };
  const copyReminder = async () => {
    const { data } = await api.get(`/admin/households/${hh.id}/message?type=reminder`);
    const ok = await copyText(data.message);
    if (ok) { toast.success("Reminder message copied"); }
    else { window.prompt("Copy the reminder below (press Ctrl/Cmd+C):", data.message); }
  };
  const copyFinal = async () => {
    const { data } = await api.get(`/admin/households/${hh.id}/message?type=final`);
    const ok = await copyText(data.message);
    if (ok) { toast.success("Final notice copied"); }
    else { window.prompt("Copy the final notice below (press Ctrl/Cmd+C):", data.message); }
  };
  const saveNote = async (note) => { await api.patch(`/admin/households/${hh.id}`, { private_note: note }); toast.success("Note saved"); onChange(); };
  const changeTier = async (tier) => { await api.patch(`/admin/households/${hh.id}`, { tier }); onChange(); };
  const saveName = async () => {
    const v = nameVal.trim();
    if (!v) return toast.error("Name can't be empty");
    await api.patch(`/admin/households/${hh.id}`, { invite_name: v });
    toast.success("Invite name updated");
    setEditName(false);
    onChange();
  };

  return (
    <div className={surface} data-testid={`household-${hh.id}`}>
      <div className="flex items-center gap-3 p-4">
        <input type="checkbox" checked={selected} onChange={onSelect} className="accent-[#C8A97E] w-4 h-4" data-testid={`select-${hh.id}`} />
        <button onClick={() => setOpen((o) => !o)} className="text-stone-500">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</button>
        <div className="flex-1 min-w-0 flex items-center gap-2.5 flex-wrap">
          {editName ? (
            <span className="inline-flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Input value={nameVal} onChange={(e) => setNameVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveName(); if (e.key === "Escape") { setNameVal(hh.invite_name); setEditName(false); } }} className="h-8 w-60 bg-[#241812] border-[#3a2a20] text-stone-200 text-sm" data-testid={`household-name-edit-${hh.id}`} autoFocus />
              <button onClick={saveName} className="text-gold" data-testid={`household-name-save-${hh.id}`} title="Save"><Save className="w-4 h-4" /></button>
              <button onClick={() => { setNameVal(hh.invite_name); setEditName(false); }} className="text-stone-500 hover:text-stone-300" title="Cancel"><X className="w-4 h-4" /></button>
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 min-w-0">
              <span className="font-medium text-stone-100 truncate">{hh.invite_name}</span>
              <button onClick={(e) => { e.stopPropagation(); setEditName(true); }} className="text-stone-500 hover:text-gold shrink-0" data-testid={`household-name-edit-toggle-${hh.id}`} title="Edit invite name"><Pencil className="w-3.5 h-3.5" /></button>
            </span>
          )}
          <Badge tone="dim">Tier {hh.tier}</Badge>
          {!hh.is_active && <Badge tone="red">Inactive</Badge>}
          {(() => { const p = replyPill(attending, declined, awaiting, hh.invite_status); return <span className={`text-xs px-2.5 py-1 rounded-full border ${p.cls}`} data-testid={`reply-status-${hh.id}`}>{p.label}</span>; })()}
        </div>
      </div>

      {open && (
        <div className="border-t border-[#3a2a20] p-4 space-y-4">
          <div className="space-y-2">
            {hh.guests.map((g) => (
              <div key={g.id} className="flex flex-wrap items-center gap-2 rounded-md bg-[#1c120d] p-3" data-testid={`admin-guest-${g.id}`}>
                <div className="flex-1 min-w-[160px] flex items-center gap-2">
                  <EditableName g={g} onSave={(v) => patchGuest(g.id, { name: v }).then(() => toast.success("Name saved"))} />
                  {g.is_plus_one && <Badge tone="gold">Plus one</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => patchGuest(g.id, { attendance: "attending" })}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md border text-sm font-medium ${g.attendance === "attending" ? "bg-emerald-600 text-white border-emerald-600" : "border-[#3a2a20] text-stone-400 hover:border-emerald-500"}`}
                    data-testid={`set-attending-${g.id}`}><Check className="w-4 h-4" /> In</button>
                  <button onClick={() => patchGuest(g.id, { attendance: "declined" })}
                    className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-md border text-sm font-medium ${g.attendance === "declined" ? "bg-red-600/80 text-white border-red-600" : "border-[#3a2a20] text-stone-400 hover:border-red-500"}`}
                    data-testid={`set-declined-${g.id}`}><X className="w-4 h-4" /> Out</button>
                  {g.attendance !== "awaiting" && (
                    <button onClick={() => patchGuest(g.id, { attendance: "awaiting" })} className="text-xs text-stone-500 hover:text-stone-300 px-1" data-testid={`set-awaiting-${g.id}`}>Clear</button>
                  )}
                  <button onClick={() => deleteGuest(g.id, g.name)} className="p-2 rounded-md border border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid={`delete-guest-${g.id}`} title="Remove guest"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
            <AddGuestInline hhId={hh.id} onDone={onChange} />
          </div>

          {hh.guest_note && <p className="text-sm text-stone-400 italic">Guest note: {hh.guest_note}</p>}
          <NoteEdit initial={hh.private_note} onSave={saveNote} />

          <p className="text-xs text-stone-500">Invitation: {STATUS[hh.invite_status]}{hh.invitation_sent_at ? ` · sent ${new Date(hh.invitation_sent_at).toLocaleDateString()}` : ""}</p>
          <div className="flex flex-wrap gap-2 items-center">
            <ActionBtn onClick={toggleActive} testid={`toggle-active-${hh.id}`}>{hh.is_active ? "Deactivate" : "Activate"}</ActionBtn>
            <ActionBtn onClick={markSent} testid={`mark-sent-${hh.id}`}><Send className="w-3.5 h-3.5" /> Mark sent</ActionBtn>
            <ActionBtn onClick={copyMessage} testid={`copy-message-${hh.id}`}><Copy className="w-3.5 h-3.5" /> Copy message</ActionBtn>
            <ActionBtn onClick={copyReminder} testid={`copy-reminder-${hh.id}`}><Copy className="w-3.5 h-3.5" /> Reminder</ActionBtn>
            <ActionBtn onClick={copyFinal} testid={`copy-final-${hh.id}`}><Copy className="w-3.5 h-3.5" /> Final notice</ActionBtn>
            <ActionBtn onClick={copyLink} testid={`copy-link-${hh.id}`}><Link2 className="w-3.5 h-3.5" /> Copy link</ActionBtn>
            <select value={hh.tier} onChange={(e) => changeTier(e.target.value)} className={selCls} data-testid={`tier-select-${hh.id}`}>
              {["A", "B", "C"].map((t) => <option key={t} value={t}>{`Tier ${t}`}</option>)}
            </select>
            <ActionBtn onClick={deleteHousehold} testid={`delete-household-${hh.id}`} danger><Trash2 className="w-3.5 h-3.5" /> Delete household</ActionBtn>
          </div>
        </div>
      )}
    </div>
  );
}

function NoteEdit({ initial, onSave }) {
  const [v, setV] = useState(initial || "");
  return (
    <div>
      <label className="text-xs uppercase tracking-wide text-stone-500">Private note</label>
      <div className="flex gap-2 mt-1">
        <Textarea value={v} onChange={(e) => setV(e.target.value)} className="bg-[#1c120d] border-[#3a2a20] text-stone-200 text-sm min-h-[42px]" placeholder="Only visible to Angeo and Gracie" />
        {v !== (initial || "") && <button onClick={() => onSave(v)} className="px-3 rounded-md bg-gold text-[#1a110c]"><Save className="w-4 h-4" /></button>}
      </div>
    </div>
  );
}

function AddGuestInline({ hhId, onDone }) {
  const [name, setName] = useState("");
  const add = async (isPlus) => {
    if (!isPlus && !name.trim()) return;
    await api.post(`/admin/households/${hhId}/guests`, { name: name.trim(), is_plus_one: isPlus });
    setName(""); onDone();
  };
  return (
    <div className="flex gap-2 items-center pt-1 flex-wrap">
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Add a guest name" className="h-8 bg-[#241812] border-[#3a2a20] text-stone-200 text-sm max-w-xs" data-testid={`add-guest-name-${hhId}`} />
      <ActionBtn onClick={() => add(false)} testid={`add-guest-${hhId}`}><Plus className="w-3.5 h-3.5" /> Add</ActionBtn>
      <ActionBtn onClick={() => add(true)} testid={`add-plusone-${hhId}`}><Plus className="w-3.5 h-3.5" /> Plus one spot</ActionBtn>
    </div>
  );
}

function AddHouseholdDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [tier, setTier] = useState("A");
  const [names, setNames] = useState("");
  const [plus, setPlus] = useState(0);
  const save = async () => {
    if (!name.trim()) return toast.error("Enter an invite name");
    await api.post("/admin/households", { invite_name: name.trim(), tier, guest_names: names.split("\n").map((n) => n.trim()).filter(Boolean), plus_count: Number(plus) || 0 });
    toast.success("Household added");
    setOpen(false); setName(""); setNames(""); setPlus(0); onDone();
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-gold text-[#1a110c] text-sm font-medium" data-testid="add-household-btn"><Plus className="w-3.5 h-3.5" /> Household</button>
      </DialogTrigger>
      <DialogContent className="bg-[#241812] border-[#3a2a20] text-stone-200">
        <DialogHeader><DialogTitle className="text-xl font-semibold">Add Household</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Invite name (e.g. Smith Family)" value={name} onChange={(e) => setName(e.target.value)} className="bg-[#1c120d] border-[#3a2a20] text-stone-200" data-testid="new-hh-name" />
          <select value={tier} onChange={(e) => setTier(e.target.value)} className="w-full bg-[#1c120d] border border-[#3a2a20] rounded-md px-3 py-2 text-stone-200">
            {["A", "B", "C"].map((t) => <option key={t} value={t}>{`Tier ${t}`}</option>)}
          </select>
          <Textarea placeholder="Guest names, one per line" value={names} onChange={(e) => setNames(e.target.value)} className="bg-[#1c120d] border-[#3a2a20] text-stone-200" data-testid="new-hh-names" />
          <div>
            <label className="text-xs uppercase tracking-wide text-stone-500">Plus one places (optional)</label>
            <Input type="number" min="0" value={plus} onChange={(e) => setPlus(e.target.value)} className="bg-[#1c120d] border-[#3a2a20] text-stone-200 mt-1" />
          </div>
        </div>
        <DialogFooter><button onClick={save} className="px-5 py-2 rounded-md bg-gold text-[#1a110c] font-medium" data-testid="save-household">Save</button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportTab({ onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const doPreview = async () => {
    if (!file) return toast.error("Choose a CSV file");
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/import/preview", fd); setPreview(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const doCommit = async () => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/import/commit", fd); toast.success(`Imported ${data.added} new, skipped ${data.skipped_duplicates} duplicates`); setPreview(null); setFile(null); onDone(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className={`${surface} p-6`} data-testid="import-tab">
      <h3 className="text-lg font-semibold text-stone-100 mb-2">Import Guest List</h3>
      <p className="text-stone-400 text-sm mb-4">Upload a CSV with the same columns as your guest spreadsheet. Households with the same invite name are detected as duplicates and skipped, so responses are never overwritten.</p>
      <div className="flex flex-wrap gap-3 items-center">
        <input type="file" accept=".csv" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} className="text-sm text-stone-400" data-testid="import-file" />
        <ActionBtn onClick={doPreview} testid="import-preview-btn"><Upload className="w-3.5 h-3.5" /> {busy ? "Working..." : "Preview"}</ActionBtn>
        {preview && <button onClick={doCommit} disabled={busy || preview.new_count === 0} className="px-4 py-2 rounded-md bg-gold text-[#1a110c] text-sm font-medium disabled:opacity-50" data-testid="import-commit-btn">Import {preview.new_count} new</button>}
      </div>
      {preview && (
        <div className="mt-5">
          <p className="text-sm text-stone-400 mb-3">{preview.total_rows} rows · {preview.new_count} new · {preview.duplicate_count} duplicates</p>
          <div className="max-h-80 overflow-auto rounded-md border border-[#3a2a20]">
            <table className="w-full text-sm">
              <thead className="bg-[#1c120d] sticky top-0"><tr className="text-left text-stone-500"><th className="p-2">Invite</th><th className="p-2">Tier</th><th className="p-2">Names</th><th className="p-2">+1</th><th className="p-2">Status</th></tr></thead>
              <tbody>
                {preview.preview.map((p, i) => (
                  <tr key={i} className={`border-t border-[#3a2a20]/60 ${p.is_duplicate ? "opacity-40" : ""}`}>
                    <td className="p-2 text-stone-100">{p.invite_name}</td>
                    <td className="p-2 text-stone-400">{p.tier}</td>
                    <td className="p-2 text-stone-400">{p.named_count}</td>
                    <td className="p-2 text-stone-400">{p.plus_count}</td>
                    <td className="p-2 text-stone-400">{p.is_duplicate ? "duplicate" : p.source_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <p className="text-stone-500">Loading...</p>;
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => { await api.put("/admin/settings", s); toast.success("Settings saved"); };
  const exportCsv = async (kind) => {
    const res = await fetch(`${API}/admin/export?kind=${kind}`, { credentials: "include" });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${kind}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  const Field = ({ label, k, area }) => (
    <div>
      <label className="text-xs uppercase tracking-wide text-stone-500">{label}</label>
      {area
        ? <Textarea value={s[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1 bg-[#1c120d] border-[#3a2a20] text-stone-200" data-testid={`setting-${k}`} />
        : <Input value={s[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1 bg-[#1c120d] border-[#3a2a20] text-stone-200" data-testid={`setting-${k}`} />}
    </div>
  );
  return (
    <div className="space-y-6" data-testid="settings-tab">
      <div className={`${surface} p-6 grid md:grid-cols-2 gap-4`}>
        <Field label="Welcome message" k="welcome_message" area />
        <Field label="Our story" k="story" area />
        <Field label="Schedule note" k="schedule_note" area />
        <Field label="Weather note" k="weather_note" area />
        <Field label="Rain note (public)" k="rain_note" />
        <Field label="Amazon registry URL" k="amazon_registry_url" />
        <Field label="The Knot fund URL (blank = coming soon)" k="knot_registry_url" />
        <Field label="RSVP deadline (display)" k="rsvp_deadline_display" />
        <Field label="RSVP deadline (YYYY-MM-DD)" k="rsvp_deadline_iso" />
        <Field label="Rain plan (private, never published)" k="rain_private_note" area />
        <div className="flex items-center gap-3 md:col-span-2">
          <Switch checked={!!s.rsvp_open} onCheckedChange={(v) => set("rsvp_open", v)} data-testid="setting-rsvp-open" />
          <span className="text-sm text-stone-300">RSVPs open (turn off to close, or on to reopen and extend)</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-3">
        <button onClick={save} className="px-6 py-2.5 rounded-md bg-gold text-[#1a110c] font-medium inline-flex items-center gap-2" data-testid="save-settings"><Save className="w-4 h-4" /> Save settings</button>
        <ActionBtn onClick={() => exportCsv("guests")} testid="export-guests"><Download className="w-3.5 h-3.5" /> Export full list</ActionBtn>
        <ActionBtn onClick={() => exportCsv("attending")} testid="export-attending"><Download className="w-3.5 h-3.5" /> Export attending (catering)</ActionBtn>
        <ActionBtn onClick={() => exportCsv("accepted_pending")} testid="export-accepted-pending"><Download className="w-3.5 h-3.5" /> Export names (accepted + pending)</ActionBtn>
      </div>
    </div>
  );
}

function ActivityTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get("/admin/change-log").then((r) => setLogs(r.data)); }, []);
  return (
    <div className={`${surface} p-6`} data-testid="activity-tab">
      <h3 className="text-lg font-semibold text-stone-100 mb-4">Change History</h3>
      <div className="space-y-2 max-h-[500px] overflow-auto">
        {logs.length === 0 && <p className="text-stone-500 text-sm">No activity yet.</p>}
        {logs.map((l) => (
          <div key={l.id} className="flex items-start gap-3 text-sm border-b border-[#3a2a20]/60 pb-2">
            <span className="text-gold text-xs whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</span>
            <span className="text-stone-500">{l.actor}</span>
            <span className="text-stone-200 whitespace-pre-line" data-testid={`activity-detail-${l.id}`}>{l.detail}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
